'use client';

import { useEffect, useRef, useState } from 'react';
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from 'react-share';

import { Button } from '../../elements';
import styles from './index.module.scss';

export interface ShareProps {
  id: any;
  text: string | null;
  label?: string;
  copy?: string;
}

export const Share = ({ id, text, label, copy, ...rest }: ShareProps) => {
  const classic = useRef<HTMLUListElement>(null);
  const current = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const url = `https://coverstori.es/${id}`;

  const share = (e: any) => {
    e.preventDefault();

    if (navigator.share) {
      navigator.share({
        title: text ?? undefined,
        url: url,
      });
    }
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback for older browsers or when clipboard API is not available
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
      } finally {
        document.body.removeChild(textArea);
      }
    }
  };

  useEffect(() => {
    if (navigator && navigator.share!) {
      if (classic && classic.current) {
        classic.current.style.display = 'none';
      }
      if (current && current.current) {
        current.current.style.display = 'inline-block';
      }
    }
  }, []);

  return (
    <section className={`${styles.share}`}>
      <ul ref={classic} className={styles.items} data-label={label}>
        <li
          className={`${styles.item} ${styles.itemTwitter}`}
          data-share="twitter"
        >
          {/* @ts-ignore */}
          <TwitterShareButton title={text ?? undefined} url={url}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 14">
              <path
                d="M15.97 1.713c-.59.259-1.22.436-1.884.516A3.305 3.305 0 0015.528.414c-.634.37-1.337.64-2.085.79a3.28 3.28 0 00-5.59 2.988A9.29 9.29 0 011.094.773a3.215 3.215 0 00-.444 1.65c0 1.14.58 2.142 1.459 2.73a3.27 3.27 0 01-1.485-.41v.04a3.282 3.282 0 002.63 3.219 3.33 3.33 0 01-1.474.056 3.291 3.291 0 003.069 2.278A6.579 6.579 0 01.78 11.741c-.26 0-.52-.016-.78-.045a9.33 9.33 0 005.038 1.473c6.036 0 9.333-4.998 9.333-9.324 0-.14 0-.28-.01-.42.64-.46 1.2-1.04 1.64-1.699l-.032-.013z"
                fillRule="nonzero"
              />
            </svg>
          </TwitterShareButton>
        </li>
        <li
          className={`${styles.item} ${styles.itemFacebook}`}
          data-share="facebook"
        >
          {/* @ts-ignore */}
          <FacebookShareButton title={text ?? undefined} url={url}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
              <path
                d="M15.999 8a8 8 0 10-9.25 7.902v-5.59H4.72V7.999h2.03V6.237c0-2.005 1.195-3.112 3.022-3.112.875 0 1.79.156 1.79.156V5.25h-1.008c-.994 0-1.304.616-1.304 1.249v1.5h2.219l-.355 2.313H9.25v5.59A8.001 8.001 0 0016 7.999z"
                fillRule="nonzero"
              />
            </svg>
          </FacebookShareButton>
        </li>
        <li
          className={`${styles.item} ${styles.itemLinkedin}`}
          data-share="linkedin"
        >
          {/* @ts-ignore */}
          <LinkedinShareButton title={text ?? undefined} url={url}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
              <path
                d="M13.631 13.635h-2.369V9.922c0-.885-.018-2.025-1.235-2.025-1.235 0-1.424.964-1.424 1.96v3.778H6.234V6H8.51v1.04h.03c.319-.6 1.092-1.233 2.247-1.233 2.401 0 2.845 1.58 2.845 3.637v4.19zM3.558 4.955A1.375 1.375 0 012.183 3.58a1.376 1.376 0 111.375 1.377zm1.188 8.68H2.37V6h2.376v7.635zM14.816 0H1.182C.528 0 0 .516 0 1.153v13.694C0 15.485.528 16 1.18 16h13.635c.652 0 1.185-.515 1.185-1.153V1.153C16 .516 15.467 0 14.815 0h.002z"
                fillRule="nonzero"
              />
            </svg>
          </LinkedinShareButton>
        </li>
        <li
          className={`${styles.item} ${styles.itemCopy} ${
            copied ? styles.itemCopied : ''
          }`}
          data-share="copy"
          onClick={onCopy}
          style={{ cursor: 'pointer' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
            <g
              strokeWidth="1.5"
              fill="none"
              fillRule="evenodd"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9.75 11.888l-1.944 1.944a3.988 3.988 0 01-5.638-5.638L4.112 6.25M6.25 4.112l1.944-1.944a3.988 3.988 0 015.638 5.638L11.888 9.75M5.375 10.625l5.25-5.25" />
            </g>
          </svg>
          <div className={styles.itemMessage}>{copy}</div>
        </li>
      </ul>

      <div ref={current} className={styles.new}>
        <Button outline onClick={share}>
          {label}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 13 13">
            <g
              stroke="#000"
              strokeWidth="1.5"
              fill="none"
              fillRule="evenodd"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M7.556.778h4.666v4.666M12.222.778L5.445 7.556M11.222 9.889v.778c0 .86-.696 1.555-1.555 1.555H2.333c-.86 0-1.555-.696-1.555-1.555V3.333c0-.86.696-1.555 1.555-1.555h.778" />
            </g>
          </svg>
        </Button>
      </div>
    </section>
  );
};

Share.defaultProps = {
  label: 'Share',
  copy: 'Copied to clipboard',
} as Partial<ShareProps>;
